/// Component tree modelled on Flame's, trimmed to what Dino Dash uses.
///
/// Differences from Flame that matter:
/// - No transform stack. Nothing rotates or scales, so absolute positions are
///   a walk up the parent chain, which keeps collision maths trivial.
/// - No async onLoad. Every image is preloaded before the game constructs its
///   tree (see engine/assets.ts), so components build sprites synchronously.
///
/// Adds and removals are queued and flushed once per frame, matching Flame's
/// lifecycle processing. Without that, an enemy culling itself mid-iteration
/// would mutate the array being walked.

import type { Anchor, Vec2 } from './vector';
import { anchors } from './vector';

export class Component {
  priority: number;
  parent: Component | null = null;
  isMounted = false;

  protected childList: Component[] = [];
  private pendingAdds: Component[] = [];
  private pendingRemovals: Component[] = [];

  constructor(priority = 0) {
    this.priority = priority;
  }

  get children(): readonly Component[] {
    return this.childList;
  }

  add(child: Component): void {
    child.parent = this;
    this.pendingAdds.push(child);
  }

  remove(child: Component): void {
    if (!this.pendingRemovals.includes(child)) {
      this.pendingRemovals.push(child);
    }
  }

  removeFromParent(): void {
    this.parent?.remove(this);
  }

  removeWhere(test: (child: Component) => boolean): void {
    for (const child of this.childList) {
      if (test(child)) this.remove(child);
    }
    // Queued but not yet mounted children would otherwise slip through.
    this.pendingAdds = this.pendingAdds.filter((child) => !test(child));
  }

  /** Every descendant of the given type, mounted children only. */
  descendantsOfType<T extends Component>(type: new (...args: never[]) => T): T[] {
    const found: T[] = [];
    const visit = (component: Component): void => {
      for (const child of component.childList) {
        if (child instanceof type) found.push(child);
        visit(child);
      }
    };
    visit(this);
    return found;
  }

  childrenOfType<T extends Component>(type: new (...args: never[]) => T): T[] {
    return this.childList.filter((child): child is T => child instanceof type);
  }

  /** Flushes queued adds and removals across the whole subtree. */
  processLifecycle(): void {
    if (this.pendingRemovals.length > 0) {
      const removals = this.pendingRemovals;
      this.pendingRemovals = [];
      for (const child of removals) {
        const index = this.childList.indexOf(child);
        if (index < 0) continue;
        this.childList.splice(index, 1);
        child.isMounted = false;
        child.parent = null;
        child.onRemove();
      }
    }

    if (this.pendingAdds.length > 0) {
      const additions = this.pendingAdds;
      this.pendingAdds = [];
      for (const child of additions) {
        this.insertByPriority(child);
        child.isMounted = true;
        child.onMount();
      }
    }

    for (const child of this.childList) {
      child.processLifecycle();
    }
  }

  /** Sorted on insert since priorities are fixed at construction. */
  private insertByPriority(child: Component): void {
    let index = this.childList.length;
    while (index > 0 && this.childList[index - 1].priority > child.priority) {
      index -= 1;
    }
    this.childList.splice(index, 0, child);
  }

  updateTree(dt: number): void {
    this.update(dt);
    // Copied because an update may cull siblings.
    for (const child of [...this.childList]) {
      child.updateTree(dt);
    }
  }

  renderTree(ctx: CanvasRenderingContext2D, originX: number, originY: number): void {
    this.render(ctx, originX, originY);
    for (const child of this.childList) {
      child.renderTree(ctx, originX, originY);
    }
  }

  /** Absolute canvas position this component's children are laid out from. */
  absoluteOrigin(): Vec2 {
    return this.parent ? this.parent.absoluteOrigin() : { x: 0, y: 0 };
  }

  onMount(): void {}

  onRemove(): void {}

  update(_dt: number): void {}

  render(_ctx: CanvasRenderingContext2D, _originX: number, _originY: number): void {}
}

export class PositionComponent extends Component {
  position: Vec2;
  size: Vec2;
  anchor: Anchor;

  constructor(options: { position?: Vec2; size?: Vec2; anchor?: Anchor; priority?: number } = {}) {
    super(options.priority ?? 0);
    this.position = options.position ?? { x: 0, y: 0 };
    this.size = options.size ?? { x: 0, y: 0 };
    this.anchor = options.anchor ?? anchors.topLeft;
  }

  get x(): number {
    return this.position.x;
  }

  set x(value: number) {
    this.position.x = value;
  }

  get y(): number {
    return this.position.y;
  }

  set y(value: number) {
    this.position.y = value;
  }

  get width(): number {
    return this.size.x;
  }

  get height(): number {
    return this.size.y;
  }

  /** Top-left of this component's box once the anchor is applied. */
  override absoluteOrigin(): Vec2 {
    const parentOrigin = this.parent ? this.parent.absoluteOrigin() : { x: 0, y: 0 };
    return {
      x: parentOrigin.x + this.position.x - this.anchor.x * this.size.x,
      y: parentOrigin.y + this.position.y - this.anchor.y * this.size.y,
    };
  }

  override renderTree(ctx: CanvasRenderingContext2D, originX: number, originY: number): void {
    const x = originX + this.position.x - this.anchor.x * this.size.x;
    const y = originY + this.position.y - this.anchor.y * this.size.y;

    this.render(ctx, x, y);
    for (const child of this.childList) {
      child.renderTree(ctx, x, y);
    }
  }
}
